import "regenerator-runtime/runtime";
import "core-js/modules/es6.object.keys";
import "core-js/modules/web.dom.iterable";
import "core-js/modules/es6.array.iterator";
import "core-js/modules/es6.string.iterator";
import "core-js/modules/es6.promise";

function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) { try { var info = gen[key](arg); var value = info.value; } catch (error) { reject(error); return; } if (info.done) { resolve(value); } else { Promise.resolve(value).then(_next, _throw); } }

function _asyncToGenerator(fn) { return function () { var self = this, args = arguments; return new Promise(function (resolve, reject) { var gen = fn.apply(self, args); function _next(value) { asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value); } function _throw(err) { asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err); } _next(undefined); }); }; }

import PropTypes from 'prop-types';
import has from 'lodash/has';
import set from 'lodash/set';
import get from 'lodash/get';
import map from 'lodash/map';
import each from 'lodash/each';
import omit from 'lodash/omit';
import memoize from 'lodash/memoize';
import isFunction from 'lodash/isFunction';
import transform from 'lodash/transform';
import debounce from 'lodash/debounce';
import serializeError from 'serialize-error';
import hash from '../lib/hash';
import defer from '../lib/defer';
import createContextStore, { propType as contextStorePropType } from './context-store';
export default function createDataProviders(_ref) {
  var providers = _ref.providers,
      _ref$contextStore = _ref.contextStore,
      contextStore = _ref$contextStore === void 0 ? createContextStore() : _ref$contextStore;
  var store = contextStore; // holds reference to requests inflight for de-duping

  var deferredRequests = {}; // holds reference to descriptions that will be loaded on next tick

  var deferredRequestsQueue = {}; // loads provider module
  //  memoized to cache providers initialized with options

  var loadProvider = memoize(function (type) {
    return new Promise(function (resolve, reject) {
      var resolveProvider = get(providers, type);
      if (isFunction(resolveProvider) === false) throw new Error("no provider defined for type: '".concat(type, "'"));
      return resolveProvider().then(resolve, reject);
    });
  }); // returns a promise to load data for provided description

  function createDeferredRequest(id, description) {
    // create a Promise that can be resolved/rejected out of scope
    // to register as the `deferredRequest` for this description id
    var deferred = defer();
    set(deferredRequests, id, deferred); // register this data description in the queue to be loaded on next tick

    set(deferredRequestsQueue, id, description); // set the loading state for this description

    store.set(id, {
      loading: true
    });
    return deferred.promise;
  }

  function resolveDeferredRequest(id, value) {
    var deferred = deferredRequests[id];
    delete deferredRequests[id]; // set the response value in the store for this description

    store.set(id, {
      loading: false,
      value: value
    }); // resolve with node-callback-style success arguments

    deferred.resolve([null, value]);
  }

  function rejectDeferredRequest(id, error) {
    var deferred = deferredRequests[id];
    delete deferredRequests[id];
    var safeError = error;

    if (safeError && safeError instanceof Error) {
      safeError = serializeError(error);

      if (process.env.NODE_ENV !== 'development') {
        safeError = omit(safeError, ['stack']);
      }
    } // set the error state for this description


    store.set(id, {
      loading: false,
      error: safeError
    }); // resolve with node-callback-style error arguments

    deferred.resolve([error, null]);
  } // loads queued batches of descriptions from individual providers
  // registers promises in `requests` dict for each description
  // updates the data store upon request resolution/rejection


  var flushDeferredDescriptionsQueue = debounce(function () {
    var ids = map(deferredRequestsQueue, function (description, id) {
      return id;
    }); // group collections of id and description by description `type`

    var descriptionsByType = transform(ids, function (memo, id) {
      var description = deferredRequestsQueue[id];
      var type = description.type;
      var list = get(memo, type, []);
      list.push({
        id: id,
        description: description
      });
      set(memo, type, list);
      delete deferredRequestsQueue[id];
    }, {}); // go through the different type groups and send batch load requests to individual providers

    each(descriptionsByType, function (descriptionsAndIds, type) {
      // prepare batch of descriptions to pass to individual provider
      var descriptions = map(descriptionsAndIds, function (_ref2) {
        var description = _ref2.description;
        return description;
      }); // load the provider module and pass the batch of descriptions to its `load(descriptions)` method

      var providerLoadPromise = loadProvider(type).then(function (provider) {
        return provider.load(descriptions);
      }); // prepare the promises to store in the `requests` dict
      // eslint-disable-next-line no-unused-vars

      each(descriptionsAndIds, function (_ref3, i) {
        var id = _ref3.id,
            description = _ref3.description;
        // wait for the promised promises returned from the individual provider's load method
        providerLoadPromise.then(function (promises) {
          var descriptionLoadPromise = promises[i]; // attach handlers to the individual promise for this particular description
          // to resolve the deferred object for the promise inserted in the `deferredRequests` dictionary

          descriptionLoadPromise.then(function (res) {
            return resolveDeferredRequest(id, res);
          }, function (err) {
            return rejectDeferredRequest(id, err);
          });
        });
      });
    });
  }); // insert description into queue to load
  // returns promise that will be resolved with node style (err, data) callback arguments
  // de-dupes for inflight and queued descriptions

  function queueDescriptionRequest(id, description) {
    if (has(deferredRequests, id)) return deferredRequests[id].promise;
    return createDeferredRequest(id, description);
  } // initiates requests for descriptions not already loading
  // returns a collection containing id and promise for each supplied description


  function loadDescriptions(descriptions) {
    // create or retrieve promise for each description matching the order passed in
    var collection = map(descriptions, function (description) {
      var id = hash(description);
      var promise = queueDescriptionRequest(id, description);
      return {
        id: id,
        promise: promise
      };
    }); // call the debounced flush method to send batch requests to individual providers

    flushDeferredDescriptionsQueue(); // return a collection of [{ id, promise }] that
    // matches the order of the descriptions provided to this method

    return collection;
  }

  function load(descriptions) {
    var collection = loadDescriptions(descriptions);
    return Promise.all(map(collection, function (_ref4) {
      var promise = _ref4.promise;
      return promise;
    }));
  }

  function hasPending() {
    return Object.keys(deferredRequests).length > 0;
  }

  function waitForPending() {
    return _waitForPending.apply(this, arguments);
  }

  function _waitForPending() {
    _waitForPending = _asyncToGenerator(
    /*#__PURE__*/
    regeneratorRuntime.mark(function _callee() {
      return regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              _context.next = 2;
              return Promise.all(map(deferredRequests, function (_ref5) {
                var promise = _ref5.promise;
                return promise;
              }));

            case 2:
            case "end":
              return _context.stop();
          }
        }
      }, _callee, this);
    }));
    return _waitForPending.apply(this, arguments);
  }

  function extract() {
    return store.get();
  }

  function fill(data) {
    return store.set(data);
  }

  return {
    store: store,
    load: load,
    hasPending: hasPending,
    waitForPending: waitForPending,
    extract: extract,
    fill: fill,
    // for tests
    loadDescriptions: loadDescriptions
  };
}
export var propType = PropTypes.shape({
  load: PropTypes.func.isRequired,
  store: contextStorePropType.isRequired,
  hasPending: PropTypes.func.isRequired,
  waitForPending: PropTypes.func.isRequired,
  extract: PropTypes.func.isRequired,
  fill: PropTypes.func.isRequired
});
export var dataDependencyPropType = PropTypes.shape({
  loading: PropTypes.bool.isRequired,
  error: PropTypes.object,
  // TODO: better validator if possible
  value: PropTypes.any // TODO: better validator if possible

});
export var storeStatePropType = PropTypes.objectOf(dataDependencyPropType);